package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.ButtonVariant
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.concurrent.newConcurrentSet
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.render.model.ImageStyleMode
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import kotlin.jvm.JvmStatic

/**
 * Button 状态视觉覆盖属性贡献者：注册状态层 8 个 + 交互态 4 个 DSL 可选覆盖字段。
 *
 * 状态层：hoverOverlayColor / hoverOverlayOpacity / pressedOverlayColor / pressedOverlayOpacity /
 * focusOverlayColor / focusOverlayOpacity / focusBorderColor / disabledOpacity。
 *
 * 交互态：glowIntensityScale / glowSizeBoost / hoverTextBrighten / textTransform。
 *
 * 所有字段 null 表示走主题默认 / 渲染器常量，非 null 为 DSL 显式覆盖。
 */
@Suppress("TooManyFunctions")
internal object ButtonStatePropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.ButtonStatePropContributor")
    private val WARNED_INVALID_VARIANTS: MutableSet<String> = newConcurrentSet()
    private const val NULL_INVALID_VARIANT_KEY = "<null>"

    @JvmStatic
    @Suppress("CyclomaticComplexMethod", "LongMethod")
    fun contribute(m: MutableMap<String, PropBinding>) {
        m.put(
            "variant",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "variant") ?: return@writeOnly
                val variantValue = v?.toString()
                val variant = ButtonVariant.tryParse(variantValue)
                if (variant == null) {
                    warnInvalidVariantOnce(variantValue)
                    b.variant(ButtonVariant.FILLED)
                } else {
                    b.variant(variant)
                }
            },
        )

        // 状态视觉属性 Slot / Button 共用：putButtonColor / putButtonFloat 已实现 base fallback，
        // Button 元素走 Button 自身字段，其它元素（如 Slot）回退到 SlotPropContributor 注册的 binding。
        putButtonColor(m, "hoverOverlayColor", Button::hoverOverlayColor)
        putButtonFloat(m, "hoverOverlayOpacity", Button::hoverOverlayOpacity)
        putButtonColor(m, "pressedOverlayColor", Button::pressedOverlayColor)
        putButtonFloat(m, "pressedOverlayOpacity", Button::pressedOverlayOpacity)
        putButtonColor(m, "focusOverlayColor", Button::focusOverlayColor)
        putButtonFloat(m, "focusOverlayOpacity", Button::focusOverlayOpacity)
        putButtonColor(m, "focusBorderColor", Button::focusBorderColor)
        putButtonFloat(m, "disabledOpacity", Button::disabledOpacity)

        // 交互态逐实例覆盖：辉光强度倍率 / 辉光扩张像素 / hover 文字提亮比 / 文字大小写变换。
        // null 表示回退渲染器常量或主题，非 null 为 DSL 显式覆盖。
        putButtonFloat(m, "glowIntensityScale", Button::glowIntensityScale)
        putButtonFloat(m, "glowSizeBoost", Button::glowSizeBoost)
        putButtonFloat(m, "hoverTextBrighten", Button::hoverTextBrighten)
        putButtonString(m, "textTransform", Button::textTransform)

        // === Stage 3a：几何覆盖 ===

        m.put(
            "textPaddingY",
            PropBinding.writeOnly { el, v ->
                if (el !is Button) {
                    PropDiag.unsupported(LOGGER, el, "textPaddingY")
                    return@writeOnly
                }
                el.textPaddingY(v?.let { ValueCoercion.toFloat(it) })
            },
        )

        m.put(
            "cornerRadius",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                // 共享 helper：Button / Slot / 一般元素分发已在 VisualPropContributor.applyCornerRadii 里
                // 统一处理，这里只需把单值/数组解析后转为 CornerRadii 再调用。
                if (v is List<*>) {
                    val nums = mutableListOf<Number>()
                    for (item in v) {
                        if (item is Number) {
                            nums.add(item)
                        } else {
                            // 非数字静默跳过；VisualPropContributor 同 prop 会输出 warn
                            return@writeOnly
                        }
                    }
                    VisualPropContributor.applyCornerRadii(el, CornerRadii.parseCss(nums))
                    return@writeOnly
                }
                VisualPropContributor.applyCornerRadii(el, CornerRadii.uniform(ValueCoercion.toFloat(v)))
            },
        )

        // 任意角圆角
        m.put(
            "cornerRadiusTL",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                VisualPropContributor.applyCornerCorner(
                    el,
                    VisualPropContributor.CornerSide.TL,
                    ValueCoercion.toFloat(v),
                )
            },
        )
        m.put(
            "cornerRadiusTR",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                VisualPropContributor.applyCornerCorner(
                    el,
                    VisualPropContributor.CornerSide.TR,
                    ValueCoercion.toFloat(v),
                )
            },
        )
        m.put(
            "cornerRadiusBR",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                VisualPropContributor.applyCornerCorner(
                    el,
                    VisualPropContributor.CornerSide.BR,
                    ValueCoercion.toFloat(v),
                )
            },
        )
        m.put(
            "cornerRadiusBL",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                VisualPropContributor.applyCornerCorner(
                    el,
                    VisualPropContributor.CornerSide.BL,
                    ValueCoercion.toFloat(v),
                )
            },
        )

        m.put(
            "icon",
            PropBinding.writeOnly { el, v ->
                if (el !is Button) {
                    PropDiag.unsupported(LOGGER, el, "icon")
                    return@writeOnly
                }
                el.icon(v?.toString())
            },
        )

        m.put(
            "iconSize",
            PropBinding.writeOnly { el, v ->
                if (el !is Button) {
                    PropDiag.unsupported(LOGGER, el, "iconSize")
                    return@writeOnly
                }
                el.iconSize(v?.let { ValueCoercion.toFloat(it) })
            },
        )

        m.put(
            "iconPosition",
            PropBinding.writeOnly { el, v ->
                if (el !is Button) {
                    PropDiag.unsupported(LOGGER, el, "iconPosition")
                    return@writeOnly
                }
                el.iconPosition(v?.toString())
            },
        )

        m.put(
            "elevation",
            PropBinding.writeOnly { el, v ->
                if (el !is Button) {
                    PropDiag.unsupported(LOGGER, el, "elevation")
                    return@writeOnly
                }
                el.elevation(v?.let { ValueCoercion.toFloat(it) })
            },
        )

        putButtonString(m, "backgroundTexture", Button::backgroundTexture)
        putButtonString(m, "hoverBackgroundTexture", Button::hoverBackgroundTexture)
        putButtonString(m, "pressedBackgroundTexture", Button::pressedBackgroundTexture)
        putButtonString(m, "focusBackgroundTexture", Button::focusBackgroundTexture)
        putButtonString(m, "disabledBackgroundTexture", Button::disabledBackgroundTexture)
        putButtonFloat(m, "textureUvX", Button::textureUvX)
        putButtonFloat(m, "textureUvY", Button::textureUvY)
        putButtonFloat(m, "textureUvW", Button::textureUvW)
        putButtonFloat(m, "textureUvH", Button::textureUvH)
        // 状态相关 UV-Y 覆盖
        putButtonFloat(m, "hoverTextureUvY", Button::hoverTextureUvY)
        putButtonFloat(m, "pressedTextureUvY", Button::pressedTextureUvY)
        putButtonFloat(m, "focusTextureUvY", Button::focusTextureUvY)
        putButtonFloat(m, "disabledTextureUvY", Button::disabledTextureUvY)
        // 图标/文字偏移
        putButtonFloat(m, "iconOffsetX", Button::iconOffsetX)
        putButtonFloat(m, "iconOffsetY", Button::iconOffsetY)
        putButtonFloat(m, "textOffsetX", Button::textOffsetX)
        putButtonFloat(m, "textOffsetY", Button::textOffsetY)
        putButtonFloat(m, "sliceTop", Button::sliceTop)
        putButtonFloat(m, "sliceRight", Button::sliceRight)
        putButtonFloat(m, "sliceBottom", Button::sliceBottom)
        putButtonFloat(m, "sliceLeft", Button::sliceLeft)
        putButtonString(m, "gradientType", Button::gradientType)
        putButtonFloat(m, "gradientAngle", Button::gradientAngle)
        putButtonColor(m, "gradientStartColor", Button::gradientStartColor)
        putButtonColor(m, "gradientEndColor", Button::gradientEndColor)
        putButtonFloat(m, "gradientStartStop", Button::gradientStartStop)
        putButtonFloat(m, "gradientEndStop", Button::gradientEndStop)
        putButtonFloat(m, "gradientCenterX", Button::gradientCenterX)
        putButtonFloat(m, "gradientCenterY", Button::gradientCenterY)
        putButtonFloat(m, "gradientRadius", Button::gradientRadius)

        m.put(
            "textureMode",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "textureMode") ?: return@writeOnly
                if (v == null) return@writeOnly
                try {
                    b.textureMode(ImageStyleMode.valueOf(v.toString().trim().uppercase()))
                } catch (e: IllegalArgumentException) {
                    LOGGER.warn("非法 textureMode: $v")
                }
            },
        )

        // === Stage 4：Loading 状态 ===

        m.put(
            "loading",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "loading") ?: return@writeOnly
                b.loading(v != null && ValueCoercion.toBool(v))
            },
        )

        m.put(
            "loadingText",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "loadingText") ?: return@writeOnly
                b.loadingText(v?.toString())
            },
        )

        m.put(
            "loadingSpinnerKind",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "loadingSpinnerKind") ?: return@writeOnly
                b.loadingSpinnerKind(v?.toString())
            },
        )

        // === Stage 5 已迁移：hotkey 由 HotkeyPropContributor 注册为通用 prop（RFC Stage 3）。 ===

        // === Stage 6：多行文本 ===

        // multiline 由本贡献者统一登记（晚于 ControlValuePropContributor 注册会覆盖同名 key），
        // 故 TextField 多行开关分支并入此处，避免被覆盖后 TextField 写 multiline 静默无效。
        m.put(
            "multiline",
            PropBinding.writeOnly { el, v ->
                when (el) {
                    is Text -> el.multiline(v != null && ValueCoercion.toBool(v))
                    is TextField -> el.multiline(v != null && ValueCoercion.toBool(v))
                    else -> {
                        val b = requireButton(el, "multiline") ?: return@writeOnly
                        b.multiline(v != null && ValueCoercion.toBool(v))
                    }
                }
            },
        )

        // textOverflow / maxLines 已上移到 VisualPropContributor 统一注册（Text 与 Button 共用），
        // 避免在 Text 上写这两个属性时误报 unsupported。此处不再重复注册。

        m.put(
            "lineSpacing",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "lineSpacing") ?: return@writeOnly
                b.lineSpacing(v?.let { ValueCoercion.toFloat(it) })
            },
        )

        m.put(
            "textAlign",
            PropBinding.writeOnly { el, v ->
                when (el) {
                    is Text -> el.textAlign(v?.toString())
                    is TextField -> el.textAlign(v?.toString())
                    else -> {
                        val b = requireButton(el, "textAlign") ?: return@writeOnly
                        b.textAlign(v?.toString())
                    }
                }
            },
        )

        m.put(
            "verticalAlign",
            PropBinding.writeOnly { el, v ->
                val b = requireButton(el, "verticalAlign") ?: return@writeOnly
                b.verticalAlign(v?.toString())
            },
        )
    }

    private fun putButtonString(m: MutableMap<String, PropBinding>, name: String, setter: (Button, String?) -> Unit) {
        // 如果 visual / 其它 contributor 已注册同名 binding（如 gradientType），保留为 fallback：
        // 元素是 Button 走 Button setter，其他元素透传给 base，避免 Box/Column 报"不支持在 Box 上设置"。
        val base = m[name]
        m[name] = PropBinding.writeOnly { el, v ->
            if (el is Button) {
                setter(el, v?.toString())
                return@writeOnly
            }
            if (base != null) {
                base.applier.apply(el, v)
            }
        }
    }

    private fun putButtonFloat(m: MutableMap<String, PropBinding>, name: String, setter: (Button, Float?) -> Unit) {
        val base = m[name]
        m[name] = PropBinding.writeOnly { el, v ->
            if (el is Button) {
                setter(el, v?.let { ValueCoercion.toFloat(it) })
                return@writeOnly
            }
            if (base != null) {
                base.applier.apply(el, v)
            }
        }
    }

    private fun putButtonColor(m: MutableMap<String, PropBinding>, name: String, setter: (Button, Int?) -> Unit) {
        // 与 putButtonString / putButtonFloat 对称：保留 visual / 其它 contributor 注册的同名 binding 作为
        // 非 Button 元素 fallback，避免 Box / Column 等容器写同名属性时打 "不支持在 Box 上设置" warn。
        val base = m[name]
        m[name] = PropBinding.writeOnly { el, v ->
            if (el is Button) {
                setter(el, v?.let { ValueCoercion.toIntColor(it) })
                return@writeOnly
            }
            if (base != null) {
                base.applier.apply(el, v)
            }
        }
    }

    private fun requireButton(el: UIElement<*>, name: String): Button? {
        if (el is Button) return el
        PropDiag.unsupported(LOGGER, el, name)
        return null
    }

    private fun warnInvalidVariantOnce(variantValue: String?) {
        val warnKey = variantValue ?: NULL_INVALID_VARIANT_KEY
        if (WARNED_INVALID_VARIANTS.add(warnKey)) {
            LOGGER.warn("非法 Button variant: $variantValue，已回退为 filled")
        }
    }
}
